import { create } from 'zustand';
import {
  getCategories,
  getCatalogCourses,
  getFeaturedCourses,
  getRecommendationRules,
  createCategory,
  updateCategory,
  deleteCategory,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
  Category,
  CatalogCourse,
  FeaturedCourse,
  RecommendationRule,
  ActiveCourse,
  getActiveCourses,
  addFeaturedCourse,
  toggleFeaturedCourse,
  removeFeaturedCourse,
  addRecommendationRule,
  updateRecommendationRule,
  toggleRecommendationRuleStatus,
  deleteRecommendationRule,
} from '@/services/PagesServices/CatalogManagementService';

interface CatalogManagementState {
  categories: Category[] | null;
  catalogCourses: CatalogCourse[] | null;
  featuredCourses: FeaturedCourse[] | null;
  recommendationRules: RecommendationRule[] | null;
  activeCourses: ActiveCourse[] | null;
  loading: boolean;
  error: string | null;

  fetchCategories: () => Promise<void>;
  fetchCatalogCourses: (filters?: {
    categoryId?: number;
    subcategoryId?: number;
    level?: string;
    minDuration?: number;
    maxDuration?: number;
    search?: string;
  }) => Promise<void>;
  fetchFeaturedCourses: () => Promise<void>;
  fetchRecommendationRules: () => Promise<void>;
  fetchActiveCourses: (search?: string) => Promise<void>;

  // Category actions
  addCategory: (name: string, description?: string) => Promise<void>;
  editCategory: (categoryId: number, name: string, description?: string) => Promise<void>;
  removeCategory: (categoryId: number) => Promise<void>;
  toggleCategoryExpand: (id: number) => void;
  addSubcategory: (parentId: number, name: string, description?: string) => Promise<void>;
  editSubcategory: (subcategoryId: number, name: string, description?: string) => Promise<void>;
  removeSubcategory: (subcategoryId: number) => Promise<void>;

  // Featured course actions
  createFeaturedCourse: (courseId: number) => Promise<void>;
  toggleFeaturedCourseStatus: (featuredId: number) => Promise<void>;
  deleteFeaturedCourse: (featuredId: number) => Promise<void>;

  // Recommendation rule actions
  createRecommendationRule: (ruleName: string, ruleCondition: string, courseIds: number[]) => Promise<void>;
  editRecommendationRule: (ruleId: number, ruleName: string, ruleCondition: string, courseIds: number[]) => Promise<void>;
  toggleRecommendationRule: (ruleId: number) => Promise<void>;
  deleteRecommendationRule: (ruleId: number) => Promise<void>;
}

const useCatalogManagementStore = create<CatalogManagementState>((set, get) => ({
  categories: null,
  catalogCourses: null,
  featuredCourses: null,
  recommendationRules: null,
  activeCourses: null,
  loading: false,
  error: null,

  fetchCategories: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCategories();
      set({ categories: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCatalogCourses: async (filters?: {
    categoryId?: number;
    subcategoryId?: number;
    level?: string;
    minDuration?: number;
    maxDuration?: number;
    search?: string;
  }) => {
    try {
      set({ loading: true, error: null });
      const response = await getCatalogCourses(filters);

      if (response.success) {
        set({ catalogCourses: response.data || [], loading: false });
      } else {
        throw new Error(response.error?.message || 'Failed to fetch catalog courses');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchFeaturedCourses: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getFeaturedCourses();
      set({ featuredCourses: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRecommendationRules: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getRecommendationRules();
      set({ recommendationRules: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  addCategory: async (name: string, description?: string) => {
    try {
      set({ loading: true, error: null });
      const result = await createCategory(name, description);

      if (result.success && result.data) {
        const { categories } = get();
        if (categories) {
          set({
            categories: [{ ...result.data, isExpanded: false }, ...categories],
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to create category');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  editCategory: async (categoryId: number, name: string, description?: string) => {
    try {
      set({ loading: true, error: null });
      const result = await updateCategory(categoryId, name, description);

      if (result.success && result.data) {
        const { categories } = get();
        if (categories) {
          set({
            categories: categories.map(cat =>
              cat.id === categoryId ? { ...cat, ...result.data } : cat
            ),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to update category');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  removeCategory: async (categoryId: number) => {
    try {
      set({ loading: true, error: null });
      const result = await deleteCategory(categoryId);

      if (result.success) {
        const { categories } = get();
        if (categories) {
          set({
            categories: categories.filter(cat => cat.id !== categoryId),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to delete category');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  toggleCategoryExpand: (id: number) => {
    const { categories } = get();
    if (categories) {
      set({
        categories: categories.map(cat =>
          cat.id === id ? { ...cat, isExpanded: !cat.isExpanded } : cat
        ),
      });
    }
  },

  addSubcategory: async (parentId: number, name: string, description?: string) => {
    try {
      set({ loading: true, error: null });
      const result = await createSubcategory(parentId, name, description);

      if (result.success && result.data) {
        const { categories } = get();
        if (categories) {
          set({
            categories: categories.map(cat => {
              if (cat.id === parentId) {
                return {
                  ...cat,
                  subcategories: [result.data!, ...(cat.subcategories || [])],
                  isExpanded: true,
                  courseCount: cat.courseCount // Keep the parent's course count
                };
              }
              return cat;
            }),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to create subcategory');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  editSubcategory: async (subcategoryId: number, name: string, description?: string) => {
    try {
      set({ loading: true, error: null });
      const result = await updateSubcategory(subcategoryId, name, description);

      if (result.success && result.data) {
        const { categories } = get();
        if (categories) {
          set({
            categories: categories.map(cat => ({
              ...cat,
              subcategories: cat.subcategories?.map(subcat =>
                subcat.id === subcategoryId ? { ...subcat, ...result.data } : subcat
              )
            })),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to update subcategory');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  removeSubcategory: async (subcategoryId: number) => {
    try {
      set({ loading: true, error: null });
      const result = await deleteSubcategory(subcategoryId);

      if (result.success) {
        const { categories } = get();
        if (categories) {
          set({
            categories: categories.map(cat => ({
              ...cat,
              subcategories: cat.subcategories?.filter(subcat => subcat.id !== subcategoryId)
            })),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to delete subcategory');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchActiveCourses: async (search?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getActiveCourses(search);

      if (response.success) {
        set({ activeCourses: response.data || [], loading: false });
      } else {
        throw new Error(response.error?.message || 'Failed to fetch active courses');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createFeaturedCourse: async (courseId: number) => {
    try {
      set({ loading: true, error: null });
      const result = await addFeaturedCourse(courseId);

      if (result.success && result.data) {
        const { featuredCourses } = get();
        if (featuredCourses) {
          set({
            featuredCourses: [result.data, ...featuredCourses],
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to add featured course');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  toggleFeaturedCourseStatus: async (featuredId: number) => {
    try {
      set({ loading: true, error: null });
      const result = await toggleFeaturedCourse(featuredId);

      if (result.success) {
        const { featuredCourses } = get();
        if (featuredCourses) {
          set({
            featuredCourses: featuredCourses.map(course =>
              course.id === featuredId.toString()
                ? { ...course, isActive: !course.isActive }
                : course
            ),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to toggle featured course');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteFeaturedCourse: async (featuredId: number) => {
    try {
      set({ loading: true, error: null });
      const result = await removeFeaturedCourse(featuredId);

      if (result.success) {
        const { featuredCourses } = get();
        if (featuredCourses) {
          set({
            featuredCourses: featuredCourses.filter(course => course.id !== featuredId.toString()),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to remove featured course');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createRecommendationRule: async (ruleName: string, ruleCondition: string, courseIds: number[]) => {
    try {
      set({ loading: true, error: null });
      const result = await addRecommendationRule(ruleName, ruleCondition, courseIds);

      if (result.success && result.data) {
        const { recommendationRules } = get();
        if (recommendationRules) {
          set({
            recommendationRules: [result.data, ...recommendationRules],
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to add recommendation rule');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  editRecommendationRule: async (ruleId: number, ruleName: string, ruleCondition: string, courseIds: number[]) => {
    try {
      set({ loading: true, error: null });
      const result = await updateRecommendationRule(ruleId, ruleName, ruleCondition, courseIds);

      if (result.success && result.data) {
        const { recommendationRules } = get();
        if (recommendationRules) {
          set({
            recommendationRules: recommendationRules.map(rule =>
              rule.id === ruleId.toString() ? result.data! : rule
            ),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to update recommendation rule');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  toggleRecommendationRule: async (ruleId: number) => {
    try {
      set({ loading: true, error: null });
      const result = await toggleRecommendationRuleStatus(ruleId);

      if (result.success) {
        const { recommendationRules } = get();
        if (recommendationRules) {
          set({
            recommendationRules: recommendationRules.map(rule =>
              rule.id === ruleId.toString()
                ? { ...rule, status: rule.status === 'Active' ? 'Inactive' : 'Active' }
                : rule
            ),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to toggle recommendation rule');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteRecommendationRule: async (ruleId: number) => {
    try {
      set({ loading: true, error: null });
      const result = await deleteRecommendationRule(ruleId);

      if (result.success) {
        const { recommendationRules } = get();
        if (recommendationRules) {
          set({
            recommendationRules: recommendationRules.filter(rule => rule.id !== ruleId.toString()),
            loading: false
          });
        }
      } else {
        throw new Error(result.error?.message || 'Failed to delete recommendation rule');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },
}));

export default useCatalogManagementStore;
